/**
 * Executes the Storefront `collection.products(filters:)` query with cursor
 * pagination, sort mapping, and Tier-2 client-side refinement.
 *
 * The transport is injected (`GraphQLRequest`) so this module is unit-testable in
 * Node and reuses the theme's axios `createStorefrontApiClient` in the browser.
 */

import {
  buildFilters,
  isPriceFilter,
  mergePriceRanges,
  priceRangeToFilter,
  productMatchesRefinements,
} from './ProductFilterBuilder';
import { EXCLUDED_TAG } from './FilterConfig';
import {
  CollectionSort,
  FacetGroup,
  FilterPageResult,
  FilterSelection,
  GraphQLRequest,
  ProductFilterInput,
  ProductNode,
} from './types';

const API_PAGE_SIZE = 250; // Storefront max `first`
const DEFAULT_PAGE_SIZE = 48; // parity with current `paginate by 48`
const MAX_API_PAGES_PER_CALL = 20;

export function mapSort(sortValue?: string): CollectionSort {
  switch (sortValue) {
    case 'price-ascending':
      return { sortKey: 'PRICE', reverse: false };
    case 'price-descending':
      return { sortKey: 'PRICE', reverse: true };
    case 'title-ascending':
      return { sortKey: 'TITLE', reverse: false };
    case 'title-descending':
      return { sortKey: 'TITLE', reverse: true };
    case 'created-descending':
      return { sortKey: 'CREATED', reverse: true };
    case 'best-selling':
      return { sortKey: 'BEST_SELLING', reverse: false };
    default:
      return { sortKey: 'COLLECTION_DEFAULT', reverse: false };
  }
}

const PRODUCTS_QUERY = [
  'query CollectionFilter(',
  '  $handle: String!, $first: Int!, $after: String,',
  '  $sortKey: ProductCollectionSortKeys, $reverse: Boolean,',
  '  $filters: [ProductFilter!]',
  ') {',
  '  collection(handle: $handle) {',
  '    products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, filters: $filters) {',
  '      filters { id label type values { label count input } }',
  '      edges { cursor node {',
  '        id handle title vendor productType tags availableForSale createdAt onlineStoreUrl',
  '        featuredImage { url altText }',
  '        priceRange {',
  '          minVariantPrice { amount currencyCode }',
  '          maxVariantPrice { amount currencyCode }',
  '        }',
  '        compareAtPriceRange { minVariantPrice { amount currencyCode } }',
  '      } }',
  '      pageInfo { hasNextPage endCursor }',
  '    }',
  '  }',
  '}',
].join('\n');

/**
 * Minimal projection for the counting pass — just what `keepProduct` reads. `tags` is by
 * far the heaviest field (~180KB per 250 products), so it's only requested when a tag
 * refinement actually needs it; DISCONTINUED products are all unpublished, so the
 * `onlineStoreUrl` check already excludes them without it.
 */
function buildCountQuery(needsTags: boolean, needsPrice: boolean): string {
  const fields = ['onlineStoreUrl'];
  if (needsTags) fields.push('tags');
  if (needsPrice) fields.push('priceRange { minVariantPrice { amount } }');
  return [
    'query CollectionFilterCount(',
    '  $handle: String!, $first: Int!, $after: String, $filters: [ProductFilter!]',
    ') {',
    '  collection(handle: $handle) {',
    '    products(first: $first, after: $after, filters: $filters) {',
    '      edges { node { ' + fields.join(' ') + ' } }',
    '      pageInfo { hasNextPage endCursor }',
    '    }',
    '  }',
    '}',
  ].join('\n');
}

export interface FetchPageOptions {
  handle: string;
  selection: FilterSelection;
  sortValue?: string;
  pageSize?: number;
  after?: string | null;
}

function keepProduct(
  node: ProductNode,
  refinements: ReturnType<typeof buildFilters>['clientRefinements'],
  priceRanges: ReturnType<typeof buildFilters>['priceRanges'],
): boolean {
  if (node.tags && node.tags.indexOf(EXCLUDED_TAG) !== -1) return false;
  if (!node.onlineStoreUrl) return false;
  if (!productMatchesRefinements(node.tags || [], refinements)) return false;
  if (priceRanges.length > 0 && !priceInAnyRange(node, priceRanges)) return false;
  return true;
}

/**
 * Exact product total for the current API filters, read off the facet payload.
 *
 * NOT the availability facet: "In stock" and "Out of stock" both count a product that
 * has some of each variant, so summing them over-reports (105-product collection ->
 * 105 + 63 = 168). `filter.p.product_type` / `filter.p.vendor` hold exactly one value
 * per product, so their values sum to the true count — except for the facet a filter is
 * already applied to, which Shopify reports un-scoped (same-facet values OR, so it shows
 * what you *could* add). Skip those and use whichever facet is left.
 */
const FACET_VALUE_LIMIT = 100;
const FACET_COUNT_CEILING = 5000;

function totalFromFacets(
  facets: FacetGroup[],
  apiFilters: ProductFilterInput[],
): { total: number; capped: boolean } | null {
  const selfFiltered: Record<string, boolean> = {
    'filter.p.product_type': apiFilters.some((f) => (f as any).productType !== undefined),
    'filter.p.vendor': apiFilters.some((f) => (f as any).productVendor !== undefined),
  };
  const candidates = ['filter.p.product_type', 'filter.p.vendor'];

  for (let i = 0; i < candidates.length; i++) {
    const id = candidates[i];
    if (selfFiltered[id]) continue;
    const facet = facets.filter((f) => f.id === id)[0];
    if (!facet || !facet.values || facet.values.length === 0) continue;
    let total = 0;
    let capped = facet.values.length >= FACET_VALUE_LIMIT;
    facet.values.forEach((v) => {
      total += v.count;
      if (v.count >= FACET_COUNT_CEILING) capped = true;
    });
    if (total >= FACET_COUNT_CEILING) capped = true;
    return { total, capped };
  }
  return null;
}

function priceInAnyRange(
  node: ProductNode,
  ranges: ReturnType<typeof buildFilters>['priceRanges'],
): boolean {
  const min = node.priceRange ? parseFloat(node.priceRange.minVariantPrice.amount) : NaN;
  if (isNaN(min)) return false;
  for (let i = 0; i < ranges.length; i++) {
    const lo = typeof ranges[i].min === 'number' ? (ranges[i].min as number) : 0;
    const hi = typeof ranges[i].max === 'number' ? (ranges[i].max as number) : Infinity;
    if (min >= lo && min <= hi) return true;
  }
  return false;
}

export interface CountResult {
  total: number;
  /** The scan hit `MAX_API_PAGES_PER_CALL`; `total` is a floor, render it as "N+". */
  capped: boolean;
}

/**
 * Exact number of products a selection renders, for the cases where the facet total
 * can't answer it: any Tier-2 client refinement (extra tag groups, or any price bucket)
 * makes the API set a superset of what the grid shows.
 *
 * Scans the whole API result set with the cheap projection above and applies the same
 * `keepProduct` predicate the grid uses, so the number always agrees with what Load More
 * eventually reveals. Meant to run in the background after the first page has painted.
 */
export async function countFilteredTotal(
  request: GraphQLRequest,
  options: { handle: string; selection: FilterSelection },
): Promise<CountResult> {
  const { apiFilters, clientRefinements, priceRanges } = buildFilters(options.selection);
  const query = buildCountQuery(clientRefinements.length > 0, priceRanges.length > 0);

  // Disjoint price buckets are scanned one interval at a time. The grid has to use a
  // single enclosing range (one cursor-ordered stream), but that range spans the gap
  // between buckets — "$10 to $25 + $100 to $250" on mens-polos walks 3,322 products as
  // one 10..250 range vs 615 as two intervals. Counting per interval only touches what
  // can actually match, and a product's min price lands in exactly one merged interval,
  // so nothing is counted twice.
  const intervals = mergePriceRanges(priceRanges);
  const scans =
    intervals.length > 1
      ? intervals.map((interval) => ({
          filters: apiFilters.filter((f) => !isPriceFilter(f)).concat(priceRangeToFilter(interval)),
          ranges: [interval],
        }))
      : [{ filters: apiFilters, ranges: priceRanges }];

  let total = 0;
  let pages = 0;
  let capped = false;

  for (let s = 0; s < scans.length; s++) {
    let after: string | null = null;
    let apiHasNext = true;

    while (apiHasNext && pages < MAX_API_PAGES_PER_CALL) {
      const data = await request(query, {
        handle: options.handle,
        first: API_PAGE_SIZE,
        after,
        filters: scans[s].filters,
      });
      const products = data && data.collection && data.collection.products;
      if (!products) break;

      const edges = products.edges || [];
      for (let i = 0; i < edges.length; i++) {
        if (keepProduct(edges[i].node as ProductNode, clientRefinements, scans[s].ranges)) total++;
      }

      after = products.pageInfo ? products.pageInfo.endCursor : null;
      apiHasNext = !!(products.pageInfo && products.pageInfo.hasNextPage);
      pages++;
    }

    if (apiHasNext) capped = true;
  }

  return { total, capped };
}

export async function fetchFilteredPage(
  request: GraphQLRequest,
  options: FetchPageOptions,
): Promise<FilterPageResult> {
  const pageSize = options.pageSize || DEFAULT_PAGE_SIZE;
  const sort = mapSort(options.sortValue);
  const { apiFilters, clientRefinements, priceRanges } = buildFilters(options.selection);
  const refining = clientRefinements.length > 0 || priceRanges.length > 0;

  const survivors: { node: ProductNode; cursor: string }[] = [];
  let facets: FacetGroup[] = [];
  let after = options.after || null;
  let apiHasNext = false;
  let apiPages = 0;
  let firstResponse = true;

  do {
    const first = refining || apiPages > 0 ? API_PAGE_SIZE : pageSize;
    const data = await request(PRODUCTS_QUERY, {
      handle: options.handle,
      first,
      after,
      sortKey: sort.sortKey,
      reverse: sort.reverse,
      filters: apiFilters,
    });

    const products = data && data.collection && data.collection.products;
    if (!products) {
      return {
        products: [],
        facets: [],
        endCursor: null,
        hasNextPage: false,
        clientRefined: refining,
        apiTotal: null,
        apiTotalCapped: false,
      };
    }

    if (firstResponse) {
      facets = (products.filters || []) as FacetGroup[];
      firstResponse = false;
    }

    const edges = products.edges || [];
    for (let i = 0; i < edges.length; i++) {
      const node = edges[i].node as ProductNode;
      if (keepProduct(node, clientRefinements, priceRanges))
        survivors.push({ node, cursor: edges[i].cursor });
    }

    after = products.pageInfo ? products.pageInfo.endCursor : null;
    apiHasNext = !!(products.pageInfo && products.pageInfo.hasNextPage);
    apiPages++;
  } while (apiHasNext && survivors.length < pageSize && apiPages < MAX_API_PAGES_PER_CALL);

  const page = survivors.slice(0, pageSize);
  const hasNextPage = survivors.length > pageSize || apiHasNext;
  const endCursor = page.length ? page[page.length - 1].cursor : after;

  // Only meaningful without client refinement — with it the API set is a superset of
  // what actually renders, so the caller falls back to "N+" until the grid is exhausted.
  const total = refining ? null : totalFromFacets(facets, apiFilters);

  return {
    products: page.map((s) => s.node),
    facets,
    endCursor,
    hasNextPage,
    clientRefined: refining,
    apiTotal: total ? total.total : null,
    apiTotalCapped: !!(total && total.capped),
  };
}
